import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AuthAPI } from '../services/api';
import ErrorMessage from '../components/common/ErrorMessage';

const ProfilePage: React.FC = () => {
  const { user, logout, deleteAccount } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [deleteConfirming, setDeleteConfirming] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await deleteAccount();
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || err.message || 'Failed to delete account.');
      setDeleteLoading(false);
      setDeleteConfirming(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await AuthAPI.changePassword(currentPassword, newPassword);
      setSuccess('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Profile</h1>
      </div>

      <div className="d-flex justify-content-center">
        <div style={{ maxWidth: '480px', width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* Account Info */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Account Information</h5>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {(user?.firstName || user?.lastName) && (
                <div>
                  <div className="text-small text-muted" style={{ marginBottom: '2px' }}>Name</div>
                  <strong>{[user.firstName, user.lastName].filter(Boolean).join(' ')}</strong>
                </div>
              )}
              <div>
                <div className="text-small text-muted" style={{ marginBottom: '2px' }}>Email</div>
                <strong>{user?.email || '—'}</strong>
              </div>
              <div>
                <div className="text-small text-muted" style={{ marginBottom: '2px' }}>Account type</div>
                <span>{user?.oauthProvider ? `OAuth (${user.oauthProvider})` : 'Password'}</span>
              </div>
              <div>
                <div className="text-small text-muted" style={{ marginBottom: '2px' }}>Email verified</div>
                <span>{user?.verified ? '✅ Verified' : '⚠️ Not verified'}</span>
              </div>
            </div>
          </div>

          {/* Change Password */}
          {!user?.oauthProvider && (
            <div className="card">
              <div className="card-header">
                <h5 className="card-title">Change Password</h5>
              </div>
              {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
              {success && <div className="alert alert-success">{success}</div>}
              <form onSubmit={handleChangePassword}>
                <div className="form-group">
                  <label className="form-label">Current Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="current-password"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <input
                    type="password"
                    className={`form-control ${confirmPassword && confirmPassword !== newPassword ? 'is-invalid' : ''}`}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  {confirmPassword && confirmPassword !== newPassword && (
                    <div className="invalid-feedback">Passwords do not match</div>
                  )}
                </div>
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={loading || !currentPassword || !newPassword || newPassword !== confirmPassword}
                >
                  {loading ? 'Changing…' : 'Change Password'}
                </button>
              </form>
            </div>
          )}

          {/* Sign out */}
          <div className="card">
            <p className="text-muted mb-3">Sign out of your account on this device.</p>
            <button className="btn btn-outline-danger" onClick={logout}>Sign Out</button>
          </div>

          {/* Danger Zone */}
          <div className="card" style={{ borderColor: 'var(--color-danger, #dc3545)' }}>
            <div className="card-header">
              <h5 className="card-title" style={{ color: 'var(--color-danger, #dc3545)' }}>Danger Zone</h5>
            </div>
            <p className="text-muted mb-3">
              Permanently delete your account, all documents, analytics, and shared access. This cannot be undone.
            </p>
            {deleteError && <ErrorMessage message={deleteError} onDismiss={() => setDeleteError('')} />}
            {!deleteConfirming ? (
              <button
                className="btn btn-outline-danger"
                onClick={() => setDeleteConfirming(true)}
              >
                Delete Account
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <p style={{ margin: 0, fontWeight: 500 }}>
                  Are you sure? This will permanently delete your account and all associated data.
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="btn btn-danger"
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? 'Deleting…' : 'Yes, delete my account'}
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setDeleteConfirming(false)}
                    disabled={deleteLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
